/**
 * @file
 * The Accordion Item class.
 */

/* global Accordion */

import { isTag } from "../validate.js";
import { addClass, removeClass } from "../domHelpers.js";
import { TransactionalValue } from "../TransactionalValue.js";
import Component from "../Component.js";

class AccordionItem extends Component {
  /**
   * The DOM elements within the accordion item.
   *
   * @protected
   *
   * @type {Object<HTMLElement>}
   *
   * @property {HTMLElement} item    - The accordion item element.
   * @property {HTMLElement} toggle  - The controller element.
   * @property {HTMLElement} header  - The header element.
   * @property {HTMLElement} content - The content element.
   */
  _dom = {
    item: null,
    toggle: null,
    header: null,
    content: null,
  };

  /**
   * The DOM elements within the accordion item that cannot be reset or generated by the accordion item itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["item", "toggle", "header", "content"];

  /**
   * The instantiated elements within the component.
   *
   * @protected
   *
   * @type {Object<Accordion>}
   *
   * @property {Accordion} parent - The parent accordion containing this item.
   */
  _elements = {
    parent: null,
  };

  /**
   * The open state of the accordion.
   *
   * @protected
   *
   * @type {TransactionalValue}
   */
  _open = new TransactionalValue(false);

  /**
   * The locked state of the accordions item.
   *
   * If locked, the accordion item cannot be closed.
   *
   * @protected
   *
   * @type {TransactionalValue}
   */
  _locked = new TransactionalValue(false);

  /**
   * Custom events that can be triggered throughout the accordion item.
   *
   * @protected
   *
   * @type {Object<CustomEvent>}
   */
  _events = {
    /**
     * The event that is triggered when the accordion item is shown.
     *
     * @event grauplAccordionItemExpand
     *
     * @type {CustomEvent}
     *
     * @property {boolean}               bubbles     - A flag to bubble the event
     * @property {Object<AccordionItem>} detail      - The details object containing the accordion item itself.
     * @property {AccordionItem}         detail.item - The accordion item.
     */
    expand: new CustomEvent("grauplAccordionItemExpand", {
      bubbles: true,
      detail: {
        item: this,
      },
    }),
    /**
     * The event that is triggered when the accordion item is hidden.
     *
     * @event grauplAccordionItemCollapse
     *
     * @type {CustomEvent}
     *
     * @property {boolean}               bubbles     - A flag to bubble the event
     * @property {Object<AccordionItem>} detail      - The details object containing the accordion item itself.
     * @property {AccordionItem}         detail.item - The accordion item.
     */
    collapse: new CustomEvent("grauplAccordionItemCollapse", {
      bubbles: true,
      detail: {
        item: this,
      },
    }),
  };

  /**
   * Constructs a new AccordionItem.
   *
   * @param {object}               options                             - The options object.
   * @param {HTMLElement}          options.accordionItemElement        - The accordion item element.
   * @param {HTMLElement}          options.accordionItemToggleElement  - The toggle element.
   * @param {HTMLElement}          options.accordionItemHeaderElement  - The header element.
   * @param {HTMLElement}          options.accordionItemContentElement - The content element.
   * @param {Accordion}            [options.parentAccordion = null]    - The accordion containing this item.
   */
  constructor({
    accordionItemElement,
    accordionItemToggleElement,
    accordionItemHeaderElement,
    accordionItemContentElement,
    parentAccordion = null,
  }) {
    super({
      prefix: parentAccordion.prefix,
      key: parentAccordion.key,
    });

    // Set DOM elements.
    this._dom.item = accordionItemElement;
    this._dom.toggle = accordionItemToggleElement;
    this._dom.header = accordionItemHeaderElement;
    this._dom.content = accordionItemContentElement;

    // Set the accordion elements.
    this._elements.parent = parentAccordion;
  }

  /**
   * Initializes the accordion item.
   */
  initialize() {
    // Set up the DOM.
    this._setIds();
    this._setAriaAttributes();

    // Set the initial state of the accordion item.
    if (this.dom.toggle.getAttribute("aria-expanded") === "true") {
      this.show({ force: true, emit: false, transition: false });
    } else {
      this.hide({ force: true, emit: false, transition: false });
    }
  }

  /**
   * The open state of the accordion.
   *
   * @readonly
   *
   * @type {object}
   *
   * @see _open
   */
  get isOpen() {
    return this._open.value;
  }

  /**
   * The locked state of the accordions item.
   *
   * If locked, the accordion item cannot be closed.
   *
   * @readonly
   *
   * @type {boolean}
   *
   * @see _locked
   */
  get isLocked() {
    return this._locked.value;
  }

  /**
   * Sets the IDs for the accordion item and it's elements if they don't exist.
   *
   * The generated IDs use the parent accordion's key and follows the pattern:
   *  - Accordion item: `accordion-item-{key}-{index}`
   *  - Accordion item toggle: `accordion-item-toggle-{key}-{index}`
   *  - Accordion item content: `accordion-item-content-{key}-{index}`
   */
  _setIds() {
    // Get the required information for IDs.
    const { key } = this.elements.parent;
    const index = this.elements.parent.dom.accordionItems.indexOf(
      this.dom.item
    );

    this.dom.item.id = this.dom.item.id || `accordion-item-${key}-${index}`;
    this.dom.toggle.id =
      this.dom.toggle.id || `accordion-item-toggle-${key}-${index}`;
    this.dom.header.id =
      this.dom.header.id || `accordion-item-header-${key}-${index}`;
    this.dom.content.id =
      this.dom.content.id || `accordion-item-content-${key}-${index}`;
  }

  /**
   * Sets the ARIA attributes for the accordion item and it's elements.
   */
  _setAriaAttributes() {
    // Set the ARIA attributes for the accordion item toggle.
    // If the toggle is not a button, then set the role to "button".
    if (!isTag("button", { toggle: this.dom.toggle })) {
      this.dom.toggle.setAttribute("role", "button");
    }

    // If aria-expanded is not explicitly set to "true", then set it to "false".
    if (this.dom.toggle.getAttribute("aria-expanded") !== "true") {
      this.dom.toggle.setAttribute("aria-expanded", "false");
    }

    // Set the aria-controls attribute for the toggle.
    this.dom.toggle.setAttribute("aria-controls", this.dom.content.id);

    // Set the ARIA attributes for the accordion item content.
    // If the content is not a section, then set the role to "region".
    if (!isTag("section", { content: this.dom.content })) {
      this.dom.content.setAttribute("role", "region");
    }

    // Set the aria-labelledby attribute for the content.
    this.dom.content.setAttribute("aria-labelledby", this.dom.toggle.id);
  }

  /**
   * Reveal the accordion item.
   *
   * Sets the accordion item's `aria-expanded` to "true", adds the
   * open class to the item, and removes the closed class from the item.
   *
   * @protected
   *
   * @fires grauplAccordionItemExpand
   *
   * @param {Object<boolean>} [options = {}]              - Options for revealing the accordion item.
   * @param {boolean}         [options.emit = true]       - Emit the activate event once revealed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _reveal({ emit = true, transition = true } = {}) {
    const { closeClass, openClass, transitionClass, openDuration } =
      this.elements.parent;

    // Set aria-selected to true when hiding accordion item.
    this.dom.toggle.setAttribute("aria-expanded", "true");

    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the hide class,
    // add the show class, and finally remove the transition class.
    //
    // If `transition` is false, then it doesn't matter if the transition class
    // is set. Do not use the transition.
    if (transition && transitionClass !== "") {
      addClass(transitionClass, this.dom.item);

      requestAnimationFrame(() => {
        removeClass(closeClass, this.dom.item);

        this.dom.item.style.height = `${this.dom.header.getBoundingClientRect().height}px`;

        requestAnimationFrame(() => {
          addClass(openClass, this.dom.item);

          this.dom.item.style.height = `${this.dom.header.getBoundingClientRect().height + this.dom.content.getBoundingClientRect().height}px`;

          requestAnimationFrame(() => {
            this._setTimeout(() => {
              removeClass(transitionClass, this.dom.item);

              this.dom.item.style.height = "";
            }, openDuration);
          });
        });
      });
    } else {
      // Add the show class
      addClass(openClass, this.dom.item);

      // Remove the hide class.
      removeClass(closeClass, this.dom.item);
    }

    this.dom.content.removeAttribute("inert");

    if (emit) {
      this._dispatchEvent("expand", this.dom.item);
    }
  }

  /**
   * Conseals the accordion item.
   *
   * Sets the accordion item's `aria-expanded` to "false", adds the
   * close class to the item, and removes the open class from the item.
   *
   * @protected
   *
   * @fires grauplAccordionItemCollapse
   *
   * @param {Object<boolean>} [options = {}]              - Options for concealing the accordion item.
   * @param {boolean}         [options.emit = true]       - Emit the deactivate event once concealed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _conceal({ emit = true, transition = true } = {}) {
    const { closeClass, openClass, transitionClass, closeDuration } =
      this.elements.parent;

    // Set aria-selected to false when hiding accordion item.
    this.dom.toggle.setAttribute("aria-expanded", "false");

    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the show class,
    // add the hide class, and finally remove the transition class.
    //
    // If `transition` is false, then it doesn't matter if the transition class
    // is set. Do not use the transition.
    if (transition && transitionClass !== "") {
      addClass(transitionClass, this.dom.item);

      this.dom.item.style.height = `${this.dom.item.getBoundingClientRect().height}px`;

      requestAnimationFrame(() => {
        removeClass(openClass, this.dom.item);

        this.dom.item.style.height = `${this.dom.header.getBoundingClientRect().height}px`;

        requestAnimationFrame(() => {
          addClass(closeClass, this.dom.item);

          requestAnimationFrame(() => {
            this._setTimeout(() => {
              removeClass(transitionClass, this.dom.item);

              this.dom.item.style.height = "";
            }, closeDuration);
          });
        });
      });
    } else {
      // Add the hide class
      addClass(closeClass, this.dom.item);

      // Remove the show class.
      removeClass(openClass, this.dom.item);
    }

    this.dom.content.setAttribute("inert", "true");

    if (emit) {
      this._dispatchEvent("collapse", this.dom.item);
    }
  }

  /**
   * Shows the accordion item.
   *
   * @param {Object<boolean>} [options = {}]                  - The options for showing the accordion item.
   * @param {boolean}         [options.force = false]         - Whether to force the open action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   * @param {boolean}         [options.emit = true]           - Emit the deactivate event once revealed.
   * @param {boolean}         [options.transition = true]     - Respect the transition class.
   */
  show({
    force = false,
    preserveState = false,
    emit = true,
    transition = true,
  } = {}) {
    if (this.isOpen && !force) return;

    this._reveal({ emit, transition });

    // Set the open state.
    this._open.value = true;

    if (!preserveState) {
      this._open.commit();
    }

    // If the parent accordion only allows a single item to be open at a time,
    // then close all other items.
    if (!this.elements.parent.allowMultipleExpand) {
      this.unlockSiblings();
      this.closeSiblings();
    }

    // If the parent accordion requires at least one item to be open, and this
    // is the only open item, then lock it. Otherwise, unlock all siblings.
    if (!this.elements.parent.allowNoExpand) {
      if (this.elements.parent.openAccordionItems.length <= 1) {
        this.lock();
      } else {
        this.unlockSiblings();
      }
    }
  }

  /**
   * Hides the accordion item.
   *
   * @param {Object<boolean>} [options = {}]                  - The options for hiding the accordion item.
   * @param {boolean}         [options.force = false]         - Whether to force the hide action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the open state.
   * @param {boolean}         [options.emit = true]           - Emit the deactivate event once consealed.
   * @param {boolean}         [options.transition = true]     - Respect the transition class.
   */
  hide({
    force = false,
    preserveState = false,
    emit = true,
    transition = true,
  } = {}) {
    if (!this.isOpen && !force) return;

    if (
      !this.elements.parent.allowNoExpand &&
      this.elements.parent.openAccordionItems.length <= 1
    ) {
      return;
    }

    this._conceal({ emit, transition });

    // Set the open state.
    this._open.value = false;

    if (!preserveState) {
      this._open.commit();
    }

    // If the parent accordion requires at least one item to be open, and this was
    // the second to last open item, then lock to last open item.
    if (
      !this.elements.parent.allowNoExpand &&
      this.elements.parent.openAccordionItems.length === 1
    ) {
      this.elements.parent.openAccordionItems[0].lock();
    }
  }

  /**
   * Toggle the accordion item.
   */
  toggle() {
    this.isOpen ? this.hide() : this.show();
  }

  /**
   * Focuses the accordion item.
   */
  focus() {
    this.dom.toggle.focus();
  }

  /**
   * Blurs the accordion item.
   */
  blur() {
    this.dom.toggle.blur();
  }

  /**
   * Locks the accordion item.
   */
  lock() {
    this._locked.value = true;
    this._locked.commit();
    this.dom.toggle.setAttribute("disabled", "true");
  }

  /**
   * Unlocks the accordion item.
   */
  unlock() {
    this._locked.value = false;
    this._locked.commit();
    this.dom.toggle.removeAttribute("disabled");
  }

  /**
   * Closes the siblings of the accordion item.
   */
  closeSiblings() {
    if (this.elements.parent) {
      this.elements.parent.elements.accordionItems.forEach((item) => {
        if (item !== this) {
          item.hide();
        }
      });
    }
  }

  /**
   * Unlocks the siblings of the accordion item.
   */
  unlockSiblings() {
    if (this.elements.parent) {
      this.elements.parent.elements.accordionItems.forEach((item) => {
        if (item !== this) {
          item.unlock();
        }
      });
    }
  }
}

export default AccordionItem;
